import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    CERT_FIELD_OPTIONS,
    CERT_FIELD_TRUST_AFFINITY,
    USER_PROPERTY_MAP,
} from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/certificate-based/form';

export interface BindingDialogData {
    binding?: {
        x509CertificateField: string;
        userProperty: string;
        trustAffinityLevel: 'low' | 'high';
        priority: number;
    };
    usedCertFields: string[];
    editIndex?: number;
    requiredAffinity?: 'low' | 'high';
}

export interface BindingDialogResult {
    x509CertificateField: string;
    userProperty: string;
    trustAffinityLevel: 'low' | 'high';
    priority: number;
}

@Component({
    selector: 'app-binding-dialog',
    templateUrl: './binding-dialog.component.html',
})
export class BindingDialogComponent implements OnInit {
    form: FormGroup;
    certFieldOptions = CERT_FIELD_OPTIONS;
    userPropertyOptions: string[] = [];

    get isEdit(): boolean {
        return this.data.editIndex !== undefined;
    }

    get requiredAffinity(): 'low' | 'high' | undefined {
        return this.data.requiredAffinity;
    }

    constructor(
        public dialogRef: MatDialogRef<BindingDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: BindingDialogData,
    ) {}

    ngOnInit(): void {
        const binding = this.data.binding;

        this.form = new FormGroup({
            x509CertificateField: new FormControl(
                { value: binding?.x509CertificateField ?? null, disabled: this.isEdit },
                [Validators.required],
            ),
            userProperty: new FormControl(binding?.userProperty ?? this.userPropertyOptions[0], Validators.required),
        });

        // Initialize userPropertyOptions based on current cert field
        const currentCertField = binding?.x509CertificateField;
        if (currentCertField) {
            this.userPropertyOptions = USER_PROPERTY_MAP[currentCertField] ?? ['certificateUserIds'];
        }

        // Only subscribe to cert field changes in add mode
        if (!this.isEdit) {
            this.form.get('x509CertificateField')?.valueChanges.subscribe((field) => {
                this.userPropertyOptions = USER_PROPERTY_MAP[field] ?? ['certificateUserIds'];
                const currentProp = this.form.get('userProperty')?.value;
                if (!this.userPropertyOptions.includes(currentProp)) {
                    this.form.get('userProperty')?.setValue(this.userPropertyOptions[0]);
                }
            });
        }
    }

    isCertFieldDisabled(field: string): boolean {
        // In edit mode, allow the current field; otherwise disable already-used fields
        if (this.isEdit && field === this.data.binding?.x509CertificateField) {
            return false;
        }
        return this.data.usedCertFields.includes(field);
    }

    onSave(): void {
        if (this.form.invalid) return;
        const raw = this.form.getRawValue();
        const result: BindingDialogResult = {
            x509CertificateField: raw.x509CertificateField,
            userProperty: raw.userProperty,
            trustAffinityLevel: CERT_FIELD_TRUST_AFFINITY[raw.x509CertificateField] ?? 'low',
            priority: this.data.binding?.priority ?? 0,
        };
        this.dialogRef.close(result);
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
